import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { LoginCallback } from '@okta/okta-react';
import { AppShell } from './components/layout/AppShell';
import { RequireAdmin, RequireAuth } from './auth/AuthProvider';
import { LoginPage } from './pages/LoginPage';
import { BuildersPage } from './pages/BuildersPage';
import { BuilderDetailPage } from './pages/BuilderDetailPage';
import { CommunityPage } from './pages/CommunityPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { SpendPageFallback } from './pages/SpendPageFallback';
import { SuppliersPage } from './pages/SuppliersPage';

const BidPage = lazy(() => import('./pages/BidPage').then((m) => ({ default: m.BidPage })));
const MtoLinesPage = lazy(() =>
  import('./pages/MtoLinesPage').then((m) => ({ default: m.MtoLinesPage }))
);
const CatalogModelPage = lazy(() =>
  import('./pages/CatalogModelPage').then((m) => ({ default: m.CatalogModelPage }))
);
const SpendPage = lazy(() => import('./pages/SpendPage').then((m) => ({ default: m.SpendPage })));
const MasterTablesPage = lazy(() =>
  import('./pages/MasterTablesPage').then((m) => ({ default: m.MasterTablesPage }))
);
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/login/callback', element: <LoginCallback /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/builders" replace /> },
      { path: 'builders', element: <BuildersPage /> },
      { path: 'builders/:builderId', element: <BuilderDetailPage /> },
      { path: 'suppliers', element: <SuppliersPage /> },
      { path: 'communities/:communityId', element: <CommunityPage /> },
      {
        path: 'communities/:communityId/bid',
        element: (
          <Lazy>
            <BidPage />
          </Lazy>
        ),
      },
      {
        path: 'mtos/:mtoId/lines',
        element: (
          <Lazy>
            <MtoLinesPage />
          </Lazy>
        ),
      },
      {
        path: 'models/:modelId',
        element: (
          <Lazy>
            <CatalogModelPage />
          </Lazy>
        ),
      },
      { path: 'spend', element: <Navigate to="/analysis" replace /> },
      {
        path: 'analysis',
        element: (
          <Suspense fallback={<SpendPageFallback />}>
            <SpendPage />
          </Suspense>
        ),
      },
      {
        path: 'admin',
        element: (
          <RequireAdmin>
            <Lazy>
              <AdminPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      {
        path: 'master-tables',
        element: (
          <RequireAdmin>
            <Lazy>
              <MasterTablesPage />
            </Lazy>
          </RequireAdmin>
        ),
      },
      { path: 'customers', element: <Navigate to="/builders" replace /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
